import React from 'react'

import { TextLink } from '@octane/spark'
import FormContent from 'components/FormContent'
import InformationSection from 'components/InformationSection'
import {
    FieldGroup,
    FieldInstructionBundle,
    FieldKit,
} from 'containers/FormBuilder'
import styled from 'styled-components'

import { ApplicantRuleLine } from './ApplicationFormBuilder.styled'
import {
    coapplicantWisconsinFieldNames,
    isCoapplicantWisconsinResidentFunc,
    isMarriedFunc,
    isPrimaryWisconsinResidentFunc,
    isSameAddressFunc,
    isWisconsinStateFiveFunc,
    WisconsinInstructionFields,
    wisconsinFieldNames,
} from './wisconsinInstructions'

const SectionDescription = styled.p`
    margin: 8px 0 16px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.monochrome.ravenSub};
`

const SpouseInfoHeading = styled.h4`
    margin: 16px 0 8px;
`

const WI_MARITAL_PROPERTY_LAW_URL =
    'https://dfi.wi.gov/Pages/ConsumerServices/WisconsinConsumerAct/MaritalPropertyLaw.aspx'

type Props = {
    fieldKit: FieldKit
    applicantLabel: 'Applicant' | 'Co-Applicant'
    isState5: boolean
    isMarried: boolean
    isSameAddress: boolean
    maritalStatusFields: FieldInstructionBundle
    spouseInfoFields: FieldInstructionBundle
    spouseAddressFields: FieldInstructionBundle
}

export default function WisconsinMaritalPropertySection({
    fieldKit,
    applicantLabel,
    isState5,
    isMarried,
    isSameAddress,
    maritalStatusFields,
    spouseInfoFields,
    spouseAddressFields,
}: Props): JSX.Element {
    const sectionTitle = isState5
        ? 'Wisconsin Marital Property Disclosure'
        : `${applicantLabel}'s Wisconsin Marital Property Disclosure`

    const learnMoreLink = (
        <TextLink
            as="a"
            href={WI_MARITAL_PROPERTY_LAW_URL}
            target="_blank"
            rel="noreferrer"
        >
            Learn more
        </TextLink>
    )

    return (
        <>
            <ApplicantRuleLine />
            <h3>{sectionTitle}</h3>

            {isState5 ? (
                <FormContent gridTemplate="1fr">
                    <InformationSection
                        id={`${applicantLabel.toLowerCase().replace('-', '')}WiState5Info`}
                        bannerText={
                            <>
                                Since the co-applicant on this application is
                                the borrower&apos;s spouse, additional
                                information is not needed. No notification is
                                required for Wisconsin residents under Wis.
                                Stat. § 766. {learnMoreLink}
                            </>
                        }
                    />
                </FormContent>
            ) : (
                <>
                    <SectionDescription>
                        Required for Wisconsin residents under Wis. Stat. § 766.
                        If the applicant is married, the non-applicant spouse
                        must be notified that credit is being extended. If the
                        co-applicant on this application is your spouse, this
                        section can be disregarded. {learnMoreLink}
                    </SectionDescription>

                    <FormContent gridTemplate="2fr 1fr">
                        <FieldGroup
                            fieldKit={fieldKit}
                            fieldInstructionBundle={maritalStatusFields}
                        />
                    </FormContent>

                    {isMarried && (
                        <>
                            <ApplicantRuleLine />
                            <SpouseInfoHeading>
                                {applicantLabel}&apos;s Spouse&apos;s Info
                            </SpouseInfoHeading>
                            <FormContent gridTemplate="1fr 1fr">
                                <FieldGroup
                                    fieldKit={fieldKit}
                                    fieldInstructionBundle={spouseInfoFields}
                                />
                            </FormContent>

                            {!isSameAddress && (
                                <FormContent gridTemplate="repeat(12, 1fr)">
                                    <FieldGroup
                                        fieldKit={fieldKit}
                                        fieldInstructionBundle={
                                            spouseAddressFields
                                        }
                                    />
                                </FormContent>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    )
}

type ApplicantInstructions = Pick<
    WisconsinInstructionFields,
    | 'wisconsinApplicantMaritalStatusFields'
    | 'wisconsinApplicantSpouseInfoFields'
    | 'wisconsinApplicantSpouseAddressFields'
>

type CoapplicantInstructions = Pick<
    WisconsinInstructionFields,
    | 'wisconsinCoapplicantMaritalStatusFields'
    | 'wisconsinCoapplicantSpouseInfoFields'
    | 'wisconsinCoapplicantSpouseAddressFields'
>

export function WisconsinApplicantSection({
    fieldKit,
    instructions,
}: {
    fieldKit: FieldKit
    instructions: ApplicantInstructions
}): JSX.Element | null {
    const commonProps = {
        fieldKit,
        applicantLabel: 'Applicant' as const,
        maritalStatusFields: instructions.wisconsinApplicantMaritalStatusFields,
        spouseInfoFields: instructions.wisconsinApplicantSpouseInfoFields,
        spouseAddressFields: instructions.wisconsinApplicantSpouseAddressFields,
    }

    if (isWisconsinStateFiveFunc(fieldKit.values)) {
        return (
            <WisconsinMaritalPropertySection
                {...commonProps}
                isState5
                isMarried={false}
                isSameAddress={false}
            />
        )
    }

    if (!isPrimaryWisconsinResidentFunc(fieldKit.values)) {
        return null
    }

    return (
        <WisconsinMaritalPropertySection
            {...commonProps}
            isState5={false}
            isMarried={isMarriedFunc(wisconsinFieldNames)(fieldKit.values)}
            isSameAddress={isSameAddressFunc(wisconsinFieldNames)(
                fieldKit.values,
            )}
        />
    )
}

export function WisconsinCoapplicantSection({
    fieldKit,
    instructions,
}: {
    fieldKit: FieldKit
    instructions: CoapplicantInstructions
}): JSX.Element | null {
    if (
        isWisconsinStateFiveFunc(fieldKit.values) ||
        !isCoapplicantWisconsinResidentFunc(fieldKit.values)
    ) {
        return null
    }

    return (
        <WisconsinMaritalPropertySection
            fieldKit={fieldKit}
            applicantLabel="Co-Applicant"
            isState5={false}
            isMarried={isMarriedFunc(coapplicantWisconsinFieldNames)(
                fieldKit.values,
            )}
            isSameAddress={isSameAddressFunc(coapplicantWisconsinFieldNames)(
                fieldKit.values,
            )}
            maritalStatusFields={
                instructions.wisconsinCoapplicantMaritalStatusFields
            }
            spouseInfoFields={instructions.wisconsinCoapplicantSpouseInfoFields}
            spouseAddressFields={
                instructions.wisconsinCoapplicantSpouseAddressFields
            }
        />
    )
}
